import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import type { Deal } from "../../lib/types";
import DealLink from "../DealLink";
import DealAgeChips from "../shared/DealAgeChips";
import DealCallText from "../DealCallText";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";

/**
 * Reports Tab — Jeremy's report processing + Jennifer's QC review.
 *
 * Jeremy's Queue: deals at `drone_complete` or `report_in_progress` (72-hr SLA)
 *   - Sorted oldest-first by drone_completed_date
 *   - "Ready for QC" button on every card → advances whatever stage it's in to qc_review
 *   - 4 doc-type buttons per card: click to browse OR drag-and-drop onto button to auto-label
 *
 * Jennifer's QC: deals at `qc_review` (24-hr SLA)
 * Recently Approved: last 15 approved reports
 */

// ── Doc type buttons ──
const DOC_BUTTONS = [
  { value: "drone_report", label: "Drone Report", border: "border-bardin-teal", text: "text-bardin-teal", hoverBg: "hover:bg-bardin-teal", activeBg: "bg-bardin-teal" },
  { value: "sr_weather_report", label: "SR Weather Report", border: "border-amber-500", text: "text-amber-600", hoverBg: "hover:bg-amber-500", activeBg: "bg-amber-500" },
  { value: "noaa_weather_report", label: "NOAA Weather Report", border: "border-orange-500", text: "text-orange-600", hoverBg: "hover:bg-orange-500", activeBg: "bg-orange-500" },
  { value: "companycam_report", label: "CompanyCam Report", border: "border-indigo-500", text: "text-indigo-600", hoverBg: "hover:bg-indigo-500", activeBg: "bg-indigo-500" },
] as const;

type DocType = (typeof DOC_BUTTONS)[number]["value"];

type ReportRow = Pick<
  Deal,
  | "id"
  | "homeowner_name"
  | "property_street"
  | "property_city"
  | "stage"
  | "days_in_stage"
  | "stage_entered_at"
  | "phase_entered_at"
  | "created_at"
  | "owner_id"
  | "drone_completed_date"
  | "imging_report_link"
  | "report_qc_status"
  | "report_qc_reviewed_at"
  | "qc_rejection_count"
  | "qc_rejection_reason"
  | "report_sla_met_jeremy"
  | "qc_sla_met_jennifer"
  | "updated_at"
>;

const COLS =
  "id,homeowner_name,homeowner_phone,property_street,property_city,stage,days_in_stage,stage_entered_at,phase_entered_at,created_at,owner_id,drone_completed_date,imging_report_link,report_qc_status,report_qc_reviewed_at,qc_rejection_count,qc_rejection_reason,report_sla_met_jeremy,qc_sla_met_jennifer,updated_at";

export default function ReportsTab() {
  const [deals, setDeals] = useState<ReportRow[] | null>(null);
  const [recentApproved, setRecentApproved] = useState<ReportRow[]>([]);
  const [members, setMembers] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string | null>(null); // "dealId-docType"
  const [movingToQc, setMovingToQc] = useState<Record<string, boolean>>({});
  // Track which button is being dragged over: "dealId-docType"
  const [btnDragOver, setBtnDragOver] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadRef = useRef<{ dealId: string; type: DocType } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // ── Upload ──
  async function uploadFiles(dealId: string, files: File[], type: DocType) {
    const key = `${dealId}-${type}`;
    setUploading(key);
    try {
      for (const file of files) {
        const ext = file.name.split(".").pop() ?? "pdf";
        const path = `deals/${dealId}/${type}_${Date.now()}.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from("deal-documents")
          .upload(path, file, {
            upsert: true,
            contentType: file.type || "application/octet-stream",
          });
        if (uploadErr) throw uploadErr;

        const label = DOC_BUTTONS.find((b) => b.value === type)?.label ?? type;
        // Insert into deal_documents so it shows on Deal Detail
        const { error: insertErr } = await supabase.from("deal_documents").insert({
          deal_id: dealId,
          file_name: file.name,
          file_type: type,
          storage_path: path,
          file_size: file.size,
          mime_type: file.type || null,
          uploaded_by: null, // no auth context in this tab
        });
        if (insertErr) throw insertErr;
        await supabase.from("activity_log").insert({
          deal_id: dealId,
          activity_type: "document_upload",
          title: `${label} uploaded`,
          description: file.name,
          metadata: { storage_path: path, doc_type: type },
        });
      }
      const label = DOC_BUTTONS.find((b) => b.value === type)?.label ?? type;
      alert(`${files.length > 1 ? files.length + " files" : "File"} uploaded as "${label}".`);
      reload();
    } catch (e: any) {
      alert(`Upload failed: ${e.message ?? e}`);
    } finally {
      setUploading(null);
    }
  }

  // Click button → open file picker
  function triggerUpload(dealId: string, type: DocType) {
    pendingUploadRef.current = { dealId, type };
    fileInputRef.current?.click();
  }

  function onFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const pending = pendingUploadRef.current;
    if (file && pending) {
      uploadFiles(pending.dealId, [file], pending.type);
    }
    pendingUploadRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  // ── Per-button drag & drop ──
  function onBtnDragEnter(e: React.DragEvent, key: string) {
    e.preventDefault();
    e.stopPropagation();
    setBtnDragOver(key);
  }

  function onBtnDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
  }

  function onBtnDragLeave(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    // Only reset if truly leaving the button element
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setBtnDragOver(null);
    }
  }

  function onBtnDrop(e: React.DragEvent, dealId: string, type: DocType) {
    e.preventDefault();
    e.stopPropagation();
    setBtnDragOver(null);
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    uploadFiles(dealId, Array.from(files), type);
  }

  // ── Ready for QC ──
  async function moveToQcReview(dealId: string, fromStage: string) {
    setMovingToQc((prev) => ({ ...prev, [dealId]: true }));
    try {
      const { error } = await supabase
        .from("deals")
        .update({ stage: "qc_review", days_in_stage: 0 })
        .eq("id", dealId);
      if (error) throw error;
      const fromLabel =
        fromStage === "drone_complete" ? "Awaiting Processing" : "Report In Progress";
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "stage_change",
        title: "Moved to QC Review",
        description: `Ready for QC — moved from ${fromLabel}`,
        metadata: { from: fromStage, to: "qc_review", moved_by: "jeremy_reports_tab" },
      });
      reload();
    } catch (e: any) {
      alert(`Failed to move to QC: ${e.message ?? e}`);
    } finally {
      setMovingToQc((prev) => ({ ...prev, [dealId]: false }));
    }
  }

  // ── Data loading ──
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [
        { data: pipeline, error: e1 },
        { data: approved, error: e2 },
        { data: mems },
      ] = await Promise.all([
        supabase
          .from("deals")
          .select(COLS)
          .in("stage", ["drone_complete", "report_in_progress", "qc_review"])
          .order("drone_completed_date", { ascending: true }),
        supabase
          .from("deals")
          .select(COLS)
          .eq("stage", "report_approved")
          .order("updated_at", { ascending: false })
          .limit(15),
        supabase
          .from("team_roster")
          .select("id,full_name")
          .eq("is_active", true),
      ]);
      if (e1 ?? e2) {
        if (!cancelled) setErr((e1 ?? e2)!.message);
        return;
      }
      if (!cancelled) {
        setDeals((pipeline ?? []) as unknown as ReportRow[]);
        setRecentApproved((approved ?? []) as unknown as ReportRow[]);
        const map: Record<string, string> = {};
        (mems ?? []).forEach((m) => { map[m.id] = m.full_name; });
        setMembers(map);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  // ── Queues ──
  const jeremyQueue = useMemo(
    () =>
      (deals ?? [])
        .filter((d) => d.stage === "drone_complete" || d.stage === "report_in_progress")
        .sort((a, b) => {
          const aDate = a.drone_completed_date ? new Date(a.drone_completed_date).getTime() : Infinity;
          const bDate = b.drone_completed_date ? new Date(b.drone_completed_date).getTime() : Infinity;
          return aDate - bDate;
        }),
    [deals],
  );

  const jenniferQueue = useMemo(
    () => (deals ?? []).filter((d) => d.stage === "qc_review"),
    [deals],
  );

  // ── SLA helpers ──
  function jeremySLA(d: ReportRow): { label: string; color: string } {
    if (!d.drone_completed_date) return { label: "—", color: "text-slate-400" };
    const hours = Math.floor((Date.now() - new Date(d.drone_completed_date).getTime()) / 3_600_000);
    if (hours > 72) return { label: `${Math.floor(hours / 24)}d (OVERDUE)`, color: "text-red-600 font-semibold" };
    if (hours > 48) return { label: `${hours}h`, color: "text-amber-600 font-semibold" };
    return { label: `${hours}h`, color: "text-slate-500" };
  }

  function jenniferSLA(d: ReportRow): { label: string; color: string } {
    if (!d.updated_at) return { label: "—", color: "text-slate-400" };
    const hours = Math.floor((Date.now() - new Date(d.updated_at).getTime()) / 3_600_000);
    if (hours > 24) return { label: `${hours}h (OVERDUE)`, color: "text-red-600 font-semibold" };
    if (hours > 18) return { label: `${hours}h`, color: "text-amber-600 font-semibold" };
    return { label: `${hours}h`, color: "text-slate-500" };
  }

  if (err) return <div className="bg-red-50 text-red-700 p-4 rounded">{err}</div>;
  if (!deals) return <div className="text-slate-500 py-8 text-center text-sm">Loading reports…</div>;

  return (
    <div className="space-y-6">
      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={onFileSelected} />

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2">
        <Chip color="amber">Jeremy's Queue: {jeremyQueue.length}</Chip>
        <Chip color="purple">Jennifer's QC: {jenniferQueue.length}</Chip>
        <Chip color="emerald">Recently Approved: {recentApproved.length}</Chip>
      </div>

      {/* ── JEREMY'S QUEUE ── */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold text-bardin-ink">Jeremy — Report Processing</h3>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">72-hr SLA</span>
          <span className="text-[10px] text-slate-400 ml-1">oldest first</span>
        </div>

        {jeremyQueue.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-4 text-sm text-slate-500 text-center">
            Queue clear — no reports pending processing.
          </div>
        ) : (
          <div className="space-y-2">
            {jeremyQueue.map((d) => {
              const sla = jeremySLA(d);
              const ownerName = d.owner_id ? members[d.owner_id] : null;
              const rejected = (d.qc_rejection_count ?? 0) > 0;

              return (
                <div
                  key={d.id}
                  className={
                    "bg-white rounded-xl shadow px-4 py-3 space-y-1.5 " +
                    (rejected ? "border-l-4 border-l-red-400" : "")
                  }
                >
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-bardin-ink">
                          <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                        </span>
                        <span
                          className={
                            "text-[10px] font-medium px-2 py-0.5 rounded-full " +
                            (d.stage === "drone_complete"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-amber-100 text-amber-700")
                          }
                        >
                          {d.stage === "drone_complete" ? "Awaiting Processing" : "In Progress"}
                        </span>
                        {rejected && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                            Rejected x{d.qc_rejection_count}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {isAddressLinkable(d) ? (
                          <a
                            href={mapsHref(d)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline hover:text-slate-700"
                          >
                            {[d.property_street, d.property_city].filter(Boolean).join(", ")}
                          </a>
                        ) : (
                          [d.property_street, d.property_city].filter(Boolean).join(", ") || "—"
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`text-xs ${sla.color}`}>{sla.label}</div>
                      <div className="text-[10px] text-slate-400">since drone</div>
                      <DealAgeChips deal={d} align="right" className="mt-1" />
                    </div>
                  </div>

                  {/* Detail row */}
                  <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500">
                    {ownerName && <span>RM: {ownerName.split(" ")[0]}</span>}
                    {d.drone_completed_date && (
                      <span>
                        Flown:{" "}
                        {new Date(d.drone_completed_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                    )}
                    {d.imging_report_link && <span className="text-emerald-600">IMGING linked</span>}
                    {rejected && d.qc_rejection_reason && (
                      <span className="text-red-500 italic truncate max-w-[200px]">"{d.qc_rejection_reason}"</span>
                    )}
                  </div>

                  {/* Doc upload buttons — click OR drag-and-drop onto each */}
                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    {DOC_BUTTONS.map((btn) => {
                      const btnKey = `${d.id}-${btn.value}`;
                      const isOver = btnDragOver === btnKey;
                      const isUploading = uploading === btnKey;

                      return (
                        <button
                          key={btn.value}
                          onClick={() => triggerUpload(d.id, btn.value)}
                          onDragEnter={(e) => onBtnDragEnter(e, btnKey)}
                          onDragOver={onBtnDragOver}
                          onDragLeave={onBtnDragLeave}
                          onDrop={(e) => onBtnDrop(e, d.id, btn.value)}
                          disabled={!!uploading}
                          className={
                            "text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-all disabled:opacity-50 " +
                            (isOver
                              ? `${btn.activeBg} text-white border-transparent ring-2 ring-offset-1 ring-${btn.border.replace("border-", "")} scale-105`
                              : isUploading
                                ? `${btn.activeBg} text-white border-transparent`
                                : `${btn.border} ${btn.text} ${btn.hoverBg} hover:text-white`)
                          }
                        >
                          {isUploading ? "Uploading…" : isOver ? `Drop → ${btn.label}` : btn.label}
                        </button>
                      );
                    })}

                    {/* Ready for QC — always available; advances whatever stage the card is in to qc_review */}
                    <button
                      onClick={() => moveToQcReview(d.id, d.stage ?? "report_in_progress")}
                      disabled={!!movingToQc[d.id]}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition disabled:opacity-50"
                    >
                      {movingToQc[d.id] ? "Moving…" : "Ready for QC"}
                    </button>
                    <div className="ml-auto">
                      <DealCallText dealId={d.id} homeownerName={d.homeowner_name} homeownerPhone={(d as any).homeowner_phone} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── JENNIFER'S QC ── */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold text-bardin-ink">Jennifer — QC Review</h3>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">24-hr SLA</span>
        </div>

        {jenniferQueue.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-4 text-sm text-slate-500 text-center">
            QC queue clear — no reports awaiting review.
          </div>
        ) : (
          <div className="space-y-2">
            {jenniferQueue.map((d) => {
              const sla = jenniferSLA(d);
              const ownerName = d.owner_id ? members[d.owner_id] : null;
              return (
                <div key={d.id} className="bg-white rounded-xl shadow px-4 py-3 space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-bardin-ink">
                          <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                        </span>
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                          QC Review
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {isAddressLinkable(d) ? (
                          <a
                            href={mapsHref(d)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline hover:text-slate-700"
                          >
                            {[d.property_street, d.property_city].filter(Boolean).join(", ")}
                          </a>
                        ) : (
                          [d.property_street, d.property_city].filter(Boolean).join(", ") || "—"
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`text-xs ${sla.color}`}>{sla.label}</div>
                      <div className="text-[10px] text-slate-400">in QC</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500">
                    {ownerName && <span>RM: {ownerName.split(" ")[0]}</span>}
                    {d.imging_report_link && <span className="text-emerald-600">IMGING linked</span>}
                    {(d.qc_rejection_count ?? 0) > 0 && (
                      <span className="text-red-500">Prior rejections: {d.qc_rejection_count}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── RECENTLY APPROVED ── */}
      <section>
        <h3 className="text-sm font-semibold text-bardin-ink mb-3">Recently Approved</h3>
        {recentApproved.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-4 text-sm text-slate-500 text-center">
            No recently approved reports.
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Homeowner</th>
                  <th className="text-left px-4 py-2">RM</th>
                  <th className="text-right px-4 py-2">Approved</th>
                  <th className="text-right px-4 py-2">Jeremy SLA</th>
                  <th className="text-right px-4 py-2">QC SLA</th>
                </tr>
              </thead>
              <tbody>
                {recentApproved.map((d) => {
                  const ownerName = d.owner_id ? members[d.owner_id]?.split(" ")[0] ?? "—" : "—";
                  return (
                    <tr key={d.id} className="border-t border-slate-100">
                      <td className="px-4 py-2 font-medium text-bardin-ink">
                        <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                      </td>
                      <td className="px-4 py-2 text-slate-500">{ownerName}</td>
                      <td className="px-4 py-2 text-right text-slate-500">
                        {d.updated_at
                          ? new Date(d.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                          : "—"}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {d.report_sla_met_jeremy === true ? (
                          <span className="text-emerald-600">Met</span>
                        ) : d.report_sla_met_jeremy === false ? (
                          <span className="text-red-600">Missed</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {d.qc_sla_met_jennifer === true ? (
                          <span className="text-emerald-600">Met</span>
                        ) : d.qc_sla_met_jennifer === false ? (
                          <span className="text-red-600">Missed</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Chip({ children, color }: { children: React.ReactNode; color: string }) {
  const colors: Record<string, string> = {
    amber: "bg-amber-100 text-amber-700",
    purple: "bg-purple-100 text-purple-700",
    emerald: "bg-emerald-100 text-emerald-700",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <span className={"text-xs font-medium px-2.5 py-1 rounded-full " + (colors[color] ?? colors.slate)}>
      {children}
    </span>
  );
}
